import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Form, Input, Modal, Space, Switch, Table, Tag, Tooltip, message } from 'antd'
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { confirm, translate } from '../../../core'
import { DraggableRow, DraggableTable, DragHandle } from '../../../core/component'
import { ServiceType, ServiceTypeEntry } from '../type/serviceType'
import {
  CreateServiceTypeMutation,
  DeleteServiceTypeMutation,
  GetServiceTypesQuery,
  ReorderServiceTypesMutation,
  RestoreServiceTypeMutation,
  ToggleActiveServiceTypeMutation,
  UpdateServiceTypeMutation,
} from '../graphql/serviceType.graphql'

const PAGE = { take: 1000, skip: 0 }

export const ServiceTypeList: FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [showDeleted, setShowDeleted] = useState(false)
  const [editing, setEditing] = useState<ServiceType | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [rows, setRows] = useState<ServiceTypeEntry[]>([])

  const { loading, refetch } = useQuery(GetServiceTypesQuery, {
    notifyOnNetworkStatusChange: true,
    variables: { request: { ...PAGE, includeDeleted: showDeleted } },
    onCompleted: (result) =>
      setRows(
        (result?.appointmentServiceTypesFind.data ?? []).map((it) => ({
          ...it,
          key: it.id.toString(),
        })),
      ),
    onError: (error) => messageApi.error(error.message),
  })

  const onError = (error: { message: string }) => messageApi.error(error.message)
  const onMutated = (msg: string) => {
    messageApi.success(translate(msg))
    refetch()
  }

  const [createMutation, { loading: creating }] = useMutation(CreateServiceTypeMutation, {
    onCompleted: () => {
      onMutated('save_success_message')
      closeModal()
    },
    onError,
  })
  const [updateMutation, { loading: updating }] = useMutation(UpdateServiceTypeMutation, {
    onCompleted: () => {
      onMutated('save_success_message')
      closeModal()
    },
    onError,
  })
  const [toggleMutation] = useMutation(ToggleActiveServiceTypeMutation, {
    onCompleted: () => onMutated('save_success_message'),
    onError,
  })
  const [reorderMutation] = useMutation(ReorderServiceTypesMutation, { onError })
  const [deleteMutation] = useMutation(DeleteServiceTypeMutation, {
    onCompleted: () => onMutated('remove_success_message'),
    onError,
  })
  const [restoreMutation] = useMutation(RestoreServiceTypeMutation, {
    onCompleted: () => onMutated('save_success_message'),
    onError,
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (record: ServiceType) => {
    setEditing(record)
    form.setFieldsValue({ label: record.label })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
    form.resetFields()
  }

  const handleSubmit = async () => {
    try {
      const { label } = await form.validateFields()
      if (editing)
        await updateMutation({ variables: { id: editing.id, dto: { label } } })
      else
        await createMutation({ variables: { dto: { label } } })
    } catch {
      // no-op: errors already shown to the user
    }
  }

  const handleRemove = async (record: ServiceType) => {
    const ok = await confirm(translate('remove_confirmation_message', 'service_type', '?'))
    if (!ok) return
    await deleteMutation({ variables: { id: record.id } })
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    setRows((prev) => {
      const from = prev.findIndex((r) => r.key === active.id)
      const to = prev.findIndex((r) => r.key === over.id)
      const next = arrayMove(prev, from, to)
      reorderMutation({ variables: { dto: { ids: next.map((r) => r.id) } } })
      return next
    })
  }

  const reorderDisabled = showDeleted

  return (
    <div className="p-2">
      {contextHolder}
      <Space className="w-100 justify-content-between p-2" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/appointments')}>
          {translate('back')}
        </Button>
        <Space>
          <Space>
            <Switch checked={showDeleted} onChange={setShowDeleted} />
            <span>{translate('service_type_show_deleted')}</span>
          </Space>
          <Tooltip title={translate('add')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} />
          </Tooltip>
        </Space>
      </Space>

      <DraggableTable items={rows} onDragEnd={onDragEnd}>
        <Table<ServiceTypeEntry>
          rowKey="key"
          loading={loading}
          components={reorderDisabled ? undefined : { body: { row: DraggableRow } }}
          dataSource={rows}
          pagination={false}
          columns={[
            {
              key: 'sort',
              align: 'center',
              width: 40,
              render: () => (reorderDisabled ? null : <DragHandle />),
            },
            {
              dataIndex: 'id',
              title: translate('id'),
              width: 80,
            },
            {
              dataIndex: 'label',
              title: translate('title'),
            },
            {
              dataIndex: 'isActive',
              title: translate('status'),
              width: 160,
              render: (isActive: boolean, record) =>
                record.deletedAt ? (
                  <Tag color="default">{translate('service_type_deleted')}</Tag>
                ) : (
                  <Switch
                    checked={isActive}
                    onChange={() => toggleMutation({ variables: { id: record.id } })}
                  />
                ),
            },
            {
              key: 'action',
              width: 100,
              render: (_, record) =>
                record.deletedAt ? (
                  <Tooltip title={translate('service_type_restore')}>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<UndoOutlined />}
                      onClick={() => restoreMutation({ variables: { id: record.id } })}
                    />
                  </Tooltip>
                ) : (
                  <Space>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={() => openEdit(record)}
                    />
                    <Button
                      type="text"
                      shape="circle"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemove(record)}
                    />
                  </Space>
                ),
            },
          ]}
        />
      </DraggableTable>

      <Modal
        open={modalOpen}
        title={editing ? translate('edit') : translate('add')}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={creating || updating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="label"
            label={translate('title')}
            rules={[{ required: true, message: translate('missing_field_message', 'title') }]}
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
